/* eslint-disable prettier/prettier */

// Single source of truth for every state's coverage-selection screen.
// StateCoveragesForm.vue renders these declaratively; QuoteCoverage.vue reads
// `continueDisabled` for the Calculate button and `unlimited` for pricing.
//
// ── Field entry shape ────────────────────────────────────────────────────────
//   key              form key (also the input id / name / validation key)
//   label            visible label text
//   type             'select'       policy-level: editable select on vehicle 0,
//                                   locked disabled input on vehicle 1+
//                    'select-open'  policy-level: always editable, no step gating
//                    'select-array' vehicle-specific: always editable,
//                                   model is form[key][coverageStepIndex]
//                    'display'      (default) disabled input; value is set
//                                   programmatically by QuoteCoverage's
//                                   bodilyInjuryChange / uninsuredBodilyInjuryChange
//   optionsKey       key into selectsCoverage[state], or the name of a computed
//                    when optionsComputed is set. Defaults to `key`.
//   optionsComputed  read options from a computed on StateCoveragesForm rather
//                    than straight from the store
//   spacing          wrapper spacing class; defaults to '-spacing-static-c'
//   tooltip          plain-text content for a Porsche DS icon--info tool-tip
//   showWhen         (form, coverageStepIndex) => boolean
//   packageMessage   render the "we have created the below package" note above
//                    this field once bodilyInjury is set
//   renderCustom     see the variants below
//
// ── renderCustom variants ────────────────────────────────────────────────────
//   'coll-comp-declined'  FL collision: swaps in a "Comprehensive Coverage
//                         Declined" disabled input when comprehensive is declined
//   'rental-declined-msg' FL rental: swaps in a "Coverage Declined" disabled
//                         input when comprehensive or collision is declined
//
// ── novelBlock entries ───────────────────────────────────────────────────────
//   'effective-date'  Policy Effective Date (masked input + datepicker on
//                     vehicle 0, locked input on vehicle 1+). Positional —
//                     every state places it explicitly.
//   'ga-um-type'      GA Uninsured Motorist Coverage Type radios
//   'fl-um-stacking'  FL Uninsured/Underinsured Motorist Stacking select
//   'fl-pip'          FL PIP Deductible + PIP Wage Loss Option selects
//
// ── State-level keys ─────────────────────────────────────────────────────────
//   fields             ordered field/novelBlock entries
//   packageCoverages   keys always rendered as disabled inputs
//   unlimited          state has an active Unlimited implementation; drives
//                      whether CoveragePricing (the Pay-Per-Mile / Unlimited
//                      selector) renders instead of the flat pricing summary
//
// Fee amounts and fee-disclosure copy are NOT configured here — they come from
// src/utils/fees.js, which the coverage, quote and payment screens all share.
//   continueDisabled   (form, quoteResponse) => boolean
//   onMount            (vm) => void, run after StateCoveragesForm mounts

const FULL_GLASS_TOOLTIP =
  'Comprehensive includes full glass coverage, including sunroofs and moonroofs. You are not required to pay the deductible for covered losses under full glass coverage.';

const DECLINED = 'I decline this coverage';

// Shared by the states whose Calculate button only needs liability, both
// deductibles, a date, and no outstanding quote.
const requiresDeductiblesAndDate = (f, qr) =>
  !(
    f.bodilyInjury &&
    f.comprehensiveDeductible.length &&
    f.collisionDeductible.length &&
    f.policyEffectiveDate &&
    qr == null
  );

export const STATE_COVERAGE_CONFIGS = {
  AZ: {
    unlimited: true,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured Motorist Coverage',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'underinsuredMotorist',
        label: 'Under-insured Motorist Coverage',
        type: 'select',
        optionsKey: 'underinsuredMotoristOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array'
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select-open'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true }
    ],
    packageCoverages: ['propertyDamage'],
    continueDisabled: requiresDeductiblesAndDate
  },

  // TODO: migrate CA. CaliforniaCoverages.vue was removed in the config
  // refactor; CA needs bespoke handling that the generic renderer does not yet
  // cover, namely:
  //   - comprehensive and collision each swap to a disabled "I decline this
  //     coverage" input (and are disabled) whenever uninsuredPropertyDamage is
  //     selected, and vice versa — a two-way interlock, not a showWhen
  //   - uninsuredPropertyDamage renders as a step-gated select only while both
  //     deductibles are absent/declined, otherwise as a disabled input whose
  //     placeholder falls back to 'I decline this coverage'
  //   - sendUpdate() cascades: choosing UMPD clears both deductible arrays and
  //     rental; declining either deductible forces the other to declined and
  //     rental to declined; selecting collision forces UMPD to declined
  //   - onMount default of selectsCoverage.CA.personalInjuryProtection[1]
  //   - continueDisabled: (f) => !f.policyEffectiveDate || !f.bodilyInjury ||
  //       (f.uninsuredPropertyDamage && !f.uninsuredBodilyInjury)
  //   - unlimited: false,
  //   - field order: BI, PD(display), UMBI, comp, coll, UMPD, MedPay, rental
  //     (rental only when a deductible is active), effective-date
  // CA: { ... },

  CO: {
    unlimited: true,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array'
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'propertyDamageDeductible',
        label: 'Uninsured/Underinsured Property Damage Deductible',
        type: 'select-array',
        showWhen: (f, i) =>
          !!(f.uninsuredBodilyInjury && f.collisionDeductible[i] === DECLINED)
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true }
    ],
    packageCoverages: ['propertyDamage'],
    continueDisabled: requiresDeductiblesAndDate
  },

  FL: {
    unlimited: true,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'propertyDamage',
        label: 'Property Damage',
        type: 'select',
        optionsKey: 'propertyDamageOptions',
        optionsComputed: true
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured Motorist Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e',
        showWhen: f => f.bodilyInjury !== DECLINED
      },
      {
        novelBlock: 'fl-um-stacking',
        showWhen: f =>
          f.bodilyInjury !== DECLINED && f.uninsuredBodilyInjury !== DECLINED
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array',
        showWhen: f =>
          f.bodilyInjury !== DECLINED && f.uninsuredBodilyInjury !== DECLINED
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array',
        renderCustom: 'coll-comp-declined',
        showWhen: f =>
          f.bodilyInjury !== DECLINED && f.uninsuredBodilyInjury !== DECLINED
      },
      { key: 'personalInjuryProtection', label: 'Personal Injury Protection' },
      { novelBlock: 'fl-pip' },
      {
        key: 'medicalPayments',
        label: 'Medical Payments Coverage',
        type: 'select-open'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array',
        renderCustom: 'rental-declined-msg'
      },
      { novelBlock: 'effective-date' }
    ],
    packageCoverages: ['personalInjuryProtection'],
    continueDisabled: f =>
      !!(
        !f.propertyDamage ||
        !f.policyEffectiveDate ||
        !f.personalInjuryProtection
      ),
    onMount(vm) {
      if (!vm.form.uninsuredCoverageType) {
        vm.form.uninsuredCoverageType = 'Unstacked';
        vm.sendUpdate('uninsuredCoverageType');
      }
    }
  },

  GA: {
    unlimited: false,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array',
        tooltip: FULL_GLASS_TOOLTIP
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'propertyDamageDeductible',
        label: 'Uninsured/Underinsured Property Damage Deductible',
        type: 'select-array',
        showWhen: f =>
          f.uninsuredBodilyInjury !== DECLINED &&
          f.uninsuredBodilyInjury !== null
      },
      {
        novelBlock: 'ga-um-type',
        showWhen: f =>
          f.uninsuredBodilyInjury !== DECLINED &&
          f.uninsuredBodilyInjury !== null
      },
      { novelBlock: 'effective-date' },
      {
        key: 'propertyDamage',
        label: 'Property Damage',
        showWhen: f =>
          f.bodilyInjury !== DECLINED &&
          f.bodilyInjury !== null,
        packageMessage: true
      },
      {
        key: 'uninsuredPropertyDamage',
        label: 'Uninsured Property Damage',
        showWhen: f =>
          !!(f.uninsuredBodilyInjury && f.uninsuredBodilyInjury !== DECLINED)
      },
      { key: 'personalInjuryProtection', label: 'Medical Payments' },
      {
        key: 'rentalReimbursement',
        label: 'Rental Coverage',
        showWhen: (f, i) => f.comprehensiveDeductible[i] !== DECLINED
      }
    ],
    packageCoverages: [
      'propertyDamage',
      'uninsuredPropertyDamage',
      'personalInjuryProtection',
      'rentalReimbursement'
    ],
    continueDisabled: f =>
      !!(
        !f.policyEffectiveDate ||
        !f.uninsuredBodilyInjury ||
        (!!f.uninsuredBodilyInjury.length &&
          f.uninsuredBodilyInjury !== DECLINED &&
          !(!!f.propertyDamageDeductible.length && f.uninsuredCoverageType))
      ),
    onMount(vm) {
      if (!vm.form.personalInjuryProtection) {
        vm.form.personalInjuryProtection = '$2,000';
        vm.sendUpdate('personalInjuryProtection');
      }
    }
  },

  IL: {
    unlimited: false,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array'
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      {
        key: 'propertyDamage',
        label: 'Property Damage',
        packageMessage: true,
        showWhen: (f) => !!(f.bodilyInjury)
      },
      {
        key: 'uninsuredPropertyDamage',
        label: 'Uninsured Property Damage',
        showWhen: f =>
          f.uninsuredBodilyInjury !== DECLINED &&
          f.uninsuredBodilyInjury !== null
      },
      {
        key: 'propertyDamageDeductible',
        label: 'Uninsured Property Damage Deductible',
        showWhen: f =>
          f.uninsuredBodilyInjury !== DECLINED &&
          f.uninsuredBodilyInjury !== null &&
          !!f.uninsuredPropertyDamage
      }
    ],
    packageCoverages: ['propertyDamage','uninsuredPropertyDamage', 'propertyDamageDeductible'],
    continueDisabled: requiresDeductiblesAndDate
  },

  OH: {
    unlimited: true,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array'
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select-open'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true },
      {
        key: 'uninsuredPropertyDamage',
        label: 'Uninsured Property Damage',
        showWhen: (f, i) =>
          !f.collisionDeductible[i] &&
          f.uninsuredBodilyInjury !== DECLINED &&
          f.uninsuredBodilyInjury !== null
      }
    ],
    packageCoverages: ['propertyDamage', 'uninsuredPropertyDamage'],
    continueDisabled: requiresDeductiblesAndDate
  },

  OR: {
    unlimited: false,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array',
        tooltip: FULL_GLASS_TOOLTIP
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury'
      },
      { key: 'uninsuredPropertyDamage', label: 'Uninsured Property Damage' },
      {
        key: 'personalInjuryProtection',
        label: 'Personal Injury Protection'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Coverage',
        showWhen: (f, i) => f.comprehensiveDeductible[i] !== DECLINED
      }
    ],
    packageCoverages: [
      'propertyDamage',
      'uninsuredBodilyInjury',
      'uninsuredPropertyDamage',
      'personalInjuryProtection',
      'rentalReimbursement'
    ],
    continueDisabled: requiresDeductiblesAndDate
  },

  // TODO: migrate PA. PennsylvaniaCoverages.vue was removed in the config
  // refactor; PA needs bespoke handling that the generic renderer does not yet
  // cover, namely:
  //   - a Combined First Party Benefits group: combinedFirstParty, then
  //     accidentalDeath / funeralBenefits / incomeLoss, each of which renders a
  //     disabled "Combined Benefits Selected" input while CFP is chosen, plus
  //     extraBenefits (MEDEX). Each carries its own icon--info tool-tip copy.
  //   - sendUpdate() side effect: picking accidentalDeath, funeralBenefits or
  //     incomeLoss forces combinedFirstParty to 'I decline this coverage'
  //   - a Tort Option select with tooltip, and the Uninsured/Underinsured
  //     Motorist Stacking select (reuse 'fl-um-stacking')
  //   - comprehensive/collision options capped by the vehicle's statedValue
  //     (the compSelects / collSelects computeds, already on StateCoveragesForm)
  //   - onMount default of uninsuredCoverageType = 'Stacked'
  //   - continueDisabled: (f) => !f.bodilyInjury ||
  //       !f.personalInjuryProtection || !f.tortOption
  //   - unlimited: false,
  //   - field order: BI, UMBI, UIM, stacking, tort, comp, coll, MedPay, rental,
  //     CFP group, effective-date, PD(display, packageMessage)
  //   - PA's form also needs combinedFirstParty / accidentalDeath /
  //     funeralBenefits / incomeLoss / extraBenefits seeded in data (the old
  //     component bound them in its template without declaring them)
  // PA: { ... },

  TN: {
    unlimited: true,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array',
        optionsKey: 'compSelects',
        optionsComputed: true
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array',
        optionsKey: 'collSelects',
        optionsComputed: true
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select-open'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true },
      { key: 'uninsuredPropertyDamage', label: 'Uninsured Property Damage' }
    ],
    packageCoverages: ['propertyDamage', 'uninsuredPropertyDamage'],
    continueDisabled: (f, qr) =>
      !(
        f.bodilyInjury &&
        f.propertyDamage &&
        !!f.comprehensiveDeductible.length &&
        !!f.collisionDeductible.length &&
        f.policyEffectiveDate &&
        qr == null
      )
  },

  TX: {
    unlimited: true,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured/Underinsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array'
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'personalInjuryProtection',
        label: 'Personal Injury Protection (PIP) Coverage',
        type: 'select'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true },
      {
        key: 'uninsuredPropertyDamage',
        label: 'Uninsured Property Damage',
        showWhen: f =>
          f.uninsuredBodilyInjury !== null &&
          f.uninsuredBodilyInjury !== DECLINED
      }
    ],
    packageCoverages: ['propertyDamage', 'uninsuredPropertyDamage'],
    continueDisabled: requiresDeductiblesAndDate
  },

  UT: {
    unlimited: false,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured Motorist Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'underinsuredMotorist',
        label: 'Underinsured Motorist',
        type: 'select',
        // UT intentionally reuses the UM(BI) option list for UIM.
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e',
        showWhen: f =>
          !!(f.uninsuredBodilyInjury && f.uninsuredBodilyInjury !== DECLINED)
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array'
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array'
      },
      {
        key: 'propertyDamageDeductible',
        label: 'Uninsured/Underinsured Property Damage Deductible',
        type: 'select-array',
        showWhen: (f, i) =>
          !!(f.uninsuredBodilyInjury && f.collisionDeductible[i] === DECLINED)
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' },
      { key: 'propertyDamage', label: 'Property Damage', packageMessage: true },
      {
        key: 'uninsuredPropertyDamage',
        label: 'Uninsured Property Damage',
        showWhen: f =>
          !!(f.uninsuredBodilyInjury && f.uninsuredBodilyInjury !== DECLINED)
      }
    ],
    packageCoverages: ['propertyDamage', 'uninsuredPropertyDamage'],
    continueDisabled: requiresDeductiblesAndDate
  },

  WI: {
    unlimited: false,
    fields: [
      {
        key: 'bodilyInjury',
        label: 'Bodily Injury (Per Person/Per Incident)',
        type: 'select',
        spacing: '-spacing-static-e'
      },
      { key: 'propertyDamage', label: 'Property Damage' },
      {
        key: 'uninsuredBodilyInjury',
        label: 'Uninsured Bodily Injury',
        type: 'select',
        optionsKey: 'uninsuredBodilyInjuryOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'underinsuredMotorist',
        label: 'Underinsured Motorist Coverage',
        type: 'select',
        optionsKey: 'underinsuredMotoristOptions',
        optionsComputed: true,
        spacing: '-spacing-static-e'
      },
      {
        key: 'comprehensiveDeductible',
        label: 'Comprehensive Deductible',
        type: 'select-array',
        optionsKey: 'compSelects',
        optionsComputed: true
      },
      {
        key: 'collisionDeductible',
        label: 'Collision Deductible',
        type: 'select-array',
        optionsKey: 'collSelects',
        optionsComputed: true
      },
      {
        key: 'personalInjuryProtection',
        label: 'Medical Payments Coverage',
        type: 'select-open'
      },
      {
        key: 'rentalReimbursement',
        label: 'Rental Reimbursement Limit',
        type: 'select-array'
      },
      { novelBlock: 'effective-date' }
    ],
    packageCoverages: ['propertyDamage'],
    continueDisabled: (f, qr) =>
      !(
        f.bodilyInjury &&
        f.uninsuredBodilyInjury &&
        f.propertyDamage &&
        f.policyEffectiveDate &&
        qr == null
      )
  }
};

export default STATE_COVERAGE_CONFIGS;
